import { create } from 'zustand';
import type { Connection } from '../types/connection';
import { useGlobalStore } from './globalStore';

// Use sessionStorage so each browser tab maintains its own connection independently.
// This allows users to have multiple tabs open with different connections.
const STORAGE_KEY = 'dietech_connection';
const OWNER_KEY = 'dietech_connection_owner';

/** The sessionStorage keys that hold the tab's connection pick and whose it is. */
export const CONNECTION_STORAGE_KEYS = [STORAGE_KEY, OWNER_KEY] as const;

interface ConnectionState {
  connections: Connection[];
  selectedConnection: Connection | null;
  /**
   * Bumped on every `selectConnection` call, including a call that picks the
   * connection already selected. A pick resets the facility to 0 (see
   * `applyConnection` in ConnectionSelection), so AuthTokenSetter must resolve
   * the facility again for each pick, not only when the connection id changes.
   */
  selectionId: number;
  isLoading: boolean;
  error: string | null;

  setConnections: (connections: Connection[]) => void;
  selectConnection: (connection: Connection) => void;
  clearConnection: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

function loadSavedConnection(): Connection | null {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate the saved connection has a valid connectionId
      if (parsed && typeof parsed.connectionId === 'number' && parsed.connectionId > 0) {
        return parsed;
      }
      // Invalid saved connection - clear it
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(OWNER_KEY);
    }
  } catch {
    // fall through to debug defaults
  }

  // In development, use debug defaults (matches Blazor DebugSettings:DefaultFacility=13)
  // Hardcoded fallback of 13 ensures it works even if env vars aren't loaded
  if (import.meta.env.DEV) {
    const debugFacility = Number(import.meta.env.VITE_DEBUG_FACILITY_ID) || 13;
    return {
      connectionId: debugFacility,
      facilityName: 'Debug Facility',
      facilityCityStateZip: '',
      disabled: false,
      webVersionAllowed: true,
      webVersion: '',
      // Set VITE_DEBUG_PM_VERSION to simulate a facility's PrimeManager version
      // for feature-gating in dev; empty means "unknown" → all features show.
      version: import.meta.env.VITE_DEBUG_PM_VERSION || '',
    };
  }

  return null;
}

/**
 * Check if the saved connection belongs to the current authenticated user.
 * On shared devices (iPads in kitchens), a different user may log in on the
 * same tab — their sessionStorage still holds the previous user's connection.
 * Call this after OIDC auth resolves to clear stale connections.
 */
export function validateConnectionOwner(userIdentifier: string): void {
  if (!userIdentifier) return;
  const savedOwner = sessionStorage.getItem(OWNER_KEY);
  const hasSavedConnection = sessionStorage.getItem(STORAGE_KEY) != null;
  // A saved connection with no owner recorded is treated as someone else's: there is no way to
  // tell it was this user's pick. Emails are compared without case (Keycloak keeps the case the
  // user typed).
  const otherOwner = savedOwner
    ? savedOwner.trim().toLowerCase() !== userIdentifier.trim().toLowerCase()
    : hasSavedConnection;
  if (otherOwner) {
    // Different user — clear the stale connection so MainLayout
    // redirects to /connection for a fresh selection. Their facility
    // goes too: it was picked from the previous user's mappings.
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(OWNER_KEY);
    useConnectionStore.getState().clearConnection();
    useGlobalStore.getState().clearFacility();
  }
  // Store/update the owner for future checks
  sessionStorage.setItem(OWNER_KEY, userIdentifier);
}

/**
 * A link that names another connection (Tech Log's "Work this with AI" / "Draft a menu with AI", opened in
 * the same tab) arrives in a tab that still holds the connection picked earlier. Kept, it would refuse the
 * link's facility ("You do not have access ..."). So the tab's pick and facility are dropped and the
 * picker vets the requested connection as it does for a new window (utils/facilityWindow.ts). The
 * request is still only a request: the picker honours it only if this user's connection list has it.
 * Returns true when it dropped them. Call at startup, after captureHandoffFromUrl.
 */
export function dropConnectionForHandoff(requestedConnectionId: number | null): boolean {
  if (!requestedConnectionId) return false;
  const current = useConnectionStore.getState().selectedConnection;
  if (!current || current.connectionId === requestedConnectionId) return false;
  useConnectionStore.getState().clearConnection();
  useGlobalStore.getState().clearFacility();
  return true;
}

const byName = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });

/** Facility name A-Z, then city/state/zip, then id so ties stay stable. */
export function compareConnections(a: Connection, b: Connection): number {
  return byName.compare((a.facilityName ?? '').trim(), (b.facilityName ?? '').trim())
    || byName.compare(a.facilityCityStateZip ?? '', b.facilityCityStateZip ?? '')
    || a.connectionId - b.connectionId;
}

export const useConnectionStore = create<ConnectionState>((set) => ({
  connections: [],
  selectedConnection: loadSavedConnection(),
  selectionId: 0,
  isLoading: false,
  error: null,

  setConnections: (connections) => {
    // A-Z by facility name (then city) so the sign-in picker reads in order;
    // the API returns them in whatever order SQL Server hands back.
    const allowed = connections
      .filter((c) => !c.disabled && c.webVersionAllowed)
      .sort(compareConnections);
    set((state) => {
      // If saved connection is no longer in the allowed list, clear it
      const saved = state.selectedConnection;
      if (saved && !allowed.some((c) => c.connectionId === saved.connectionId)) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(OWNER_KEY);
        return { connections: allowed, selectedConnection: null };
      }
      return { connections: allowed };
    });
  },

  selectConnection: (connection) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(connection));
    set((state) => ({ selectedConnection: connection, selectionId: state.selectionId + 1 }));
  },

  clearConnection: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(OWNER_KEY);
    set({ selectedConnection: null });
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));
